import {addTouch} from "./touch/Touch";

/**
 * The single image page (singleimage/default.php; concepts/frontend-design.md, "The single
 * image page"): the arrow keys and a swipe over the photo lead to the previous and the next
 * image, which the links #prev_image and #next_image of the toolbar name - the script only
 * follows the links the page prints. A key counts while it means nothing else: not with a
 * modifier, not typed into a field, an editable element, the player of a video or an open
 * dialog, and not while the lightbox is open, which has its own keys. A tap on the photo
 * stays what it is, the click of the link which opens the lightbox; a video has no swipe,
 * its player takes the finger. Until 7.0.0 the page knew neither.
 */
export default class SingleImagePage {

    /**
     * @param {HTMLElement} page the #singleimage element
     * @param {(url: string) => void} [navigate] what leads to another page; the location of the window by default
     */
    constructor(page, navigate = (url) => { window.location.href = url; }) {
        this.page = page;
        this.navigate = navigate;
        this.onKeyDown = this.onKeyDown.bind(this);
    }

    /**
     * Binds the single image page of the document, if it holds one.
     *
     * @param {Document|HTMLElement} root
     * @returns {SingleImagePage|null}
     */
    static bind(root = document) {
        const page = root.querySelector('#singleimage');
        if (!page) {
            return null;
        }
        const instance = new SingleImagePage(page);
        instance.bindKeys(root);
        instance.bindSwipe();
        instance.bindVideo();
        return instance;
    }

    /**
     * The video of the page counts a hit when it is played the first time, as the lightbox
     * counts one when it shows a file (the event the hit tracking of EventgalleryBehavior.js
     * listens for): a video plays on its page and no longer in the lightbox.
     */
    bindVideo() {
        const video = this.page.querySelector('#bigvideo');
        if (!video || !video.dataset.pid) {
            return;
        }
        video.addEventListener('play', () => {
            document.dispatchEvent(new CustomEvent('eventgallery-lightbox-changed', {detail: {id: parseInt(video.dataset.pid, 10)}}));
        }, {once: true});
    }

    /**
     * @param {Document|HTMLElement} root where the keys are listened for
     */
    bindKeys(root = document) {
        root.addEventListener('keydown', this.onKeyDown);
    }

    /**
     * A swipe over the photo: the finger moving to the right leads to the previous image,
     * to the left to the next one - on a right-to-left page the other way round, as the
     * toolbar stands mirrored there. The tap action is a no-op on purpose: without one the
     * touch helper clicks the target itself, and the browser's own click would open the
     * lightbox twice.
     */
    bindSwipe() {
        const photo = this.page.querySelector('#bigimagelink');
        if (!photo) {
            return;
        }
        addTouch(photo, () => this.goTo(this.towards('left')), () => this.goTo(this.towards('right')), () => {}, undefined);
    }

    /**
     * @param {KeyboardEvent} event
     */
    onKeyDown(event) {
        if (!SingleImagePage.isKeyForThePage(event)) {
            return;
        }
        const side = event.key === 'ArrowLeft' ? 'left' : (event.key === 'ArrowRight' ? 'right' : null);
        if (side && this.goTo(this.towards(side))) {
            event.preventDefault();
        }
    }

    /**
     * Which neighbour stands on a side of the page: the previous one on the left and the next
     * one on the right, mirrored on a right-to-left page, where the toolbar has the previous
     * button on the right. Until 7.0.0 ArrowLeft led to the previous image also there, against
     * the arrows of the toolbar. The finger moving to the right uncovers the left side.
     *
     * @param {'left'|'right'} side
     * @returns {'prev'|'next'}
     */
    towards(side) {
        const forward = this.isRightToLeft() ? 'left' : 'right';
        return side === forward ? 'next' : 'prev';
    }

    /**
     * @returns {boolean} whether the page reads from right to left (the dir of the page or of
     *                    an element around it, else the computed direction)
     */
    isRightToLeft() {
        const marked = this.page.closest('[dir]');
        if (marked) {
            return String(marked.getAttribute('dir')).toLowerCase() === 'rtl';
        }
        return typeof getComputedStyle === 'function' && getComputedStyle(this.page).direction === 'rtl';
    }

    /**
     * Whether a key means the page and nothing else.
     *
     * @param {KeyboardEvent} event
     * @returns {boolean}
     */
    static isKeyForThePage(event) {
        if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
            return false;
        }
        const target = event.target;
        // the player of a video seeks with the arrow keys, and a widget which walks with them
        // (tabs, radios, a slider) keeps them; a plain button - the share and the buy button
        // of the toolbar, which have the focus back when their dialog closes - means nothing
        // with an arrow key, like a link
        if (target instanceof Element && target.closest('input, textarea, select, video, audio, [contenteditable=""], [contenteditable="true"], dialog[open], [role="tab"], [role="radio"], [role="slider"], [role="spinbutton"], [role="menuitem"], [role="option"]')) {
            return false;
        }
        if (document.querySelector('dialog[open]')) {
            return false;
        }
        const lightbox = typeof window !== 'undefined' && window.Eventgallery ? window.Eventgallery.lightbox : null;
        if (lightbox && typeof lightbox.isOpen === 'function' && lightbox.isOpen()) {
            return false;
        }
        return true;
    }

    /**
     * Leads to the neighbour the toolbar links, if there is one.
     *
     * @param {'prev'|'next'} direction
     * @returns {boolean} whether the page leads somewhere
     */
    goTo(direction) {
        const link = this.page.querySelector(direction === 'prev' ? '#prev_image' : '#next_image');
        if (!(link instanceof HTMLAnchorElement) || !link.href) {
            return false;
        }
        this.navigate(link.href);
        return true;
    }
}
